<template>
  <div class="user-info-page">
    <div
      class="user-info-page__bg"
      :style="{ backgroundImage: 'url(' + bgUrl + ')' }"
    />

    <div class="user-info-page__inner">
      <h1 class="page-title">修改上传用户信息</h1>

      <div class="card">
        <div class="decor decor--sun" aria-hidden="true">
          <svg viewBox="0 0 48 48" width="40" height="40"><circle cx="24" cy="24" r="14" fill="none" stroke="#4a9eff" stroke-width="2"/><path d="M24 4v4M24 40v4M4 24h4M40 24h4M10 10l3 3M35 35l3 3M35 10l-3 3M10 38l3-3" stroke="#4a9eff" stroke-width="2" stroke-linecap="round"/></svg>
        </div>
        <div class="decor decor--plane" aria-hidden="true">
          <svg viewBox="0 0 48 32" width="44" height="28"><path d="M4 16 L40 8 L28 16 L40 24 Z" fill="none" stroke="#4a9eff" stroke-width="2" stroke-linejoin="round"/></svg>
        </div>
        <div class="decor decor--book" aria-hidden="true">
          <svg viewBox="0 0 36 40" width="32" height="36"><path d="M6 6h12v28H6zM18 6h12v28H18" fill="none" stroke="#4a9eff" stroke-width="2"/></svg>
        </div>
        <div class="decor decor--cloud decor--cloud-a" aria-hidden="true" />
        <div class="decor decor--cloud decor--cloud-b" aria-hidden="true" />

        <form class="form" @submit.prevent="onConfirm">
          <div class="row">
            <label class="label" for="uin-name">姓 名:</label>
            <input
              id="uin-name"
              v-model.trim="form.name"
              type="text"
              class="input"
            >
          </div>

          <div class="row row--gender">
            <span class="label">性 别:</span>
            <label class="radio"><input v-model="form.gender" type="radio" value="male"> 男</label>
            <label class="radio"><input v-model="form.gender" type="radio" value="female"> 女</label>
          </div>

          <div class="row">
            <label class="label" for="uin-age">年 龄:</label>
            <input
              id="uin-age"
              v-model="form.age"
              type="text"
              class="input"
              inputmode="numeric"
              maxlength="3"
              @input="form.age = form.age.replace(/\D/g, '')"
            >
          </div>

          <div class="row">
            <label class="label" for="uin-edu">学 历:</label>
            <div class="select-wrap">
              <select id="uin-edu" v-model="form.education" class="select">
                <option value="">请选择</option>
                <option value="学前">学前</option>
                <option value="小学">小学</option>
                <option value="初中">初中</option>
                <option value="高中">高中</option>
                <option value="大专">大专</option>
                <option value="本科">本科</option>
                <option value="硕士">硕士</option>
                <option value="博士">博士</option>
              </select>
            </div>
          </div>

          <div v-if="showUserType" class="row">
            <label class="label" for="uin-type">类 型:</label>
            <div class="select-wrap">
              <select id="uin-type" v-model="form.userType" class="select">
                <option value="">请选择</option>
                <option value="事业">事业</option>
                <option value="情感">情感</option>
              </select>
            </div>
          </div>

          <div class="actions">
            <button
              type="button"
              class="btn btn--ghost"
              :disabled="loading"
              @click="onCancel"
            >
              取消
            </button>
            <button
              type="submit"
              class="btn btn--primary"
              :disabled="loading"
            >
              确认
            </button>
          </div>
        </form>
      </div>
    </div>

    <transition name="fade">
      <div v-if="loading" class="loading-mask">
        <div class="loading-card">
          <div class="loading-title">正在为您准备沙盘…</div>
          <div class="progress">
            <div class="progress__bar" :style="{ width: progress + '%' }"></div>
          </div>
          <div class="loading-percent">{{ progress }}%</div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import bgUrl from '@/assets/user-info-bg.png'
import message from '@/utils/message'
import { getSandSession, saveSandSession } from '@/utils/sandSession'
import {
  isAdultSandUser,
  normalizeEducationLabel,
  normalizeUserTypeLabel
} from '@/utils/sandTableRestore'

export default {
  name: 'UserInfoUpload',
  data () {
    return {
      bgUrl,
      form: {
        name: '',
        gender: 'male',
        age: '',
        education: '',
        userType: ''
      },
      loading: false,
      progress: 0
    }
  },
  computed: {
    showUserType () {
      return isAdultSandUser(this.form.age)
    }
  },
  watch: {
    'form.age': {
      handler () {
        if (!this.showUserType) {
          this.form.userType = ''
        }
      },
      immediate: true
    }
  },
  mounted () {
    this.fillFormFromSession()
  },
  beforeDestroy () {
    this.clearProgressTimer()
  },
  methods: {
    fillFormFromSession () {
      const session = getSandSession()
      if (!session) return
      if (session.name) this.form.name = session.name
      if (session.gender) this.form.gender = session.gender
      if (session.age != null && session.age !== '') {
        this.form.age = String(session.age)
      }
      if (session.education) {
        this.form.education = normalizeEducationLabel(session.education)
      }
      if (session.userType && isAdultSandUser(this.form.age)) {
        this.form.userType = normalizeUserTypeLabel(session.userType)
      }
    },
    onCancel () {
      this.$router.push({ name: 'WorldHome' })
    },
    onConfirm () {
      if (this.loading) return
      if (!this.form.name) {
        message.warning('请填写姓名')
        return
      }
      if (!this.form.age) {
        message.warning('请填写年龄')
        return
      }
      if (!this.form.education) {
        message.warning('请选择学历')
        return
      }
      if (this.showUserType && !this.form.userType) {
        message.warning('请选择类型')
        return
      }

      const payload = this.buildSessionPayload()
      saveSandSession(payload)
      this.startLoadingThen(() => {
        this.$router.push({ name: 'IslandPlay' })
      })
    },
    buildSessionPayload () {
      const prev = getSandSession() || {}
      return {
        ...prev,
        name: this.form.name,
        gender: this.form.gender,
        age: this.form.age,
        education: this.form.education,
        userType: this.showUserType ? this.form.userType : '',
        slot: prev.slot || '1',
        restoreSand: prev.restoreSand === true
      }
    },
    startLoadingThen (after) {
      this.loading = true
      this.progress = 0
      // 30s 总长，每 300ms 推进一次：100 / (30000 / 300) ≈ 1%
      const totalMs = 30000
      const stepMs = 300
      const step = 100 / (totalMs / stepMs)
      this._progressTimer = setInterval(() => {
        this.progress = Math.min(100, this.progress + step)
        if (this.progress >= 100) {
          this.clearProgressTimer()
          this.loading = false
          after && after()
        }
      }, stepMs)
    },
    clearProgressTimer () {
      if (this._progressTimer) {
        clearInterval(this._progressTimer)
        this._progressTimer = null
      }
    }
  }
}
</script>

<style scoped>
.user-info-page {
  position: relative;
  min-height: 100vh;
  overflow: auto;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC',
    'Microsoft YaHei', sans-serif;
}

.user-info-page__bg {
  position: fixed;
  inset: 0;
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  z-index: 0;
}

.user-info-page__inner {
  position: relative;
  z-index: 1;
  max-width: 520px;
  margin: 0 auto;
  padding: 36px 20px 48px;
}

.page-title {
  margin: 0 0 20px;
  text-align: center;
  font-size: 22px;
  font-weight: 700;
  color: #0d47a1;
  letter-spacing: 0.06em;
  text-shadow: 0 1px 0 rgba(255, 255, 255, 0.8);
}

.card {
  position: relative;
  background: #fff;
  border-radius: 16px;
  padding: 32px 36px 28px;
  box-shadow: 0 12px 40px rgba(20, 60, 120, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.95);
}

.decor {
  position: absolute;
  pointer-events: none;
  opacity: 0.9;
}

.decor--sun {
  top: -8px;
  left: 8px;
}

.decor--plane {
  top: 4px;
  right: 12px;
}

.decor--book {
  left: -6px;
  top: 42%;
}

.decor--cloud {
  width: 36px;
  height: 18px;
  background: rgba(200, 230, 255, 0.85);
  border-radius: 50%;
  box-shadow: 16px 4px 0 -4px rgba(200, 230, 255, 0.75);
}

.decor--cloud-a {
  bottom: 18px;
  left: 12%;
}

.decor--cloud-b {
  bottom: 18px;
  right: 14%;
}

.form {
  position: relative;
  z-index: 2;
}

.row {
  display: flex;
  align-items: center;
  margin-bottom: 18px;
  gap: 12px;
}

.row--gender {
  flex-wrap: wrap;
}

.label {
  flex: 0 0 4.5em;
  font-size: 16px;
  color: #1565c0;
  font-weight: 600;
  letter-spacing: 0.2em;
}

.input,
.select {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  font-size: 15px;
  border: 1px solid #90caf9;
  border-radius: 8px;
  background: #fafdff;
  color: #263238;
}

.input:focus,
.select:focus {
  outline: none;
  border-color: #42a5f5;
  box-shadow: 0 0 0 2px rgba(66, 165, 245, 0.2);
}

.select-wrap {
  flex: 1;
  min-width: 0;
}

.select {
  width: 100%;
  cursor: pointer;
}

.radio {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-right: 20px;
  font-size: 15px;
  color: #37474f;
  cursor: pointer;
}

.radio input {
  accent-color: #1976d2;
}

.actions {
  display: flex;
  justify-content: center;
  gap: 24px;
  margin-top: 28px;
}

.btn {
  min-width: 120px;
  padding: 10px 24px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 10px;
  cursor: pointer;
  border: 2px solid transparent;
}

.btn--ghost {
  background: #e3f2fd;
  color: #1565c0;
  border-color: #90caf9;
}

.btn--ghost:hover {
  background: #bbdefb;
}

.btn--primary {
  background: #e3f2fd;
  color: #0d47a1;
  border-color: #64b5f6;
}

.btn--primary:hover {
  background: #bbdefb;
}

.btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background: #e3f2fd;
}

.loading-mask {
  position: fixed;
  inset: 0;
  background: rgba(13, 71, 161, 0.32);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.loading-card {
  background: #ffffff;
  border-radius: 16px;
  padding: 28px 36px 24px;
  width: min(420px, 80vw);
  box-shadow: 0 16px 48px rgba(13, 71, 161, 0.3);
  text-align: center;
}

.loading-title {
  font-size: 17px;
  font-weight: 700;
  color: #0d47a1;
  margin-bottom: 18px;
  letter-spacing: 0.04em;
}

.progress {
  width: 100%;
  height: 10px;
  background: #e3f2fd;
  border-radius: 999px;
  overflow: hidden;
}

.progress__bar {
  height: 100%;
  background: linear-gradient(90deg, #42a5f5, #1976d2);
  border-radius: inherit;
  transition: width 0.25s linear;
}

.loading-percent {
  margin-top: 10px;
  font-size: 13px;
  color: #455a64;
  font-variant-numeric: tabular-nums;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// src/views/UserInfoUpload.vue